import type { AuthoredGridPose, CoasterProject } from '../schema/index.ts';
import { AUTHORED_TRACK_CATALOG, getAuthoredDescriptor } from './catalog.ts';
import { descriptorExitPose, posesConnect } from './fixed-grid.ts';
import {
  appendAuthoredPiece,
  authoredEndPose,
  authoredStartPose,
  closeAuthoredCircuit,
  createAuthoredCandidateEndingAt,
  prependAuthoredCandidate,
} from './operations.ts';
import type { AuthoredBuildResult } from './types.ts';

export interface AuthoredFinishPlan {
  readonly descriptorIds: readonly string[];
  readonly exploredStates: number;
  readonly project: CoasterProject;
  readonly mode: 'standard';
  readonly customTailSectionIds: readonly string[];
}

export interface AuthoredFinishLimits {
  readonly maxPieces?: number;
  readonly maxStates?: number;
  readonly timeBudgetMs?: number;
  readonly validateFirstStep?: (project: CoasterProject) => string | null;
  readonly validateProject?: (project: CoasterProject) => string | null;
}

const FINISH_DESCRIPTOR_IDS = new Set([
  'straight.0_5t',
  'straight.1t',
  'straight.2t',
  'straight.3t',
  'turn.left.45.small',
  'turn.right.45.small',
  'turn.left.90.small',
  'turn.right.90.small',
  'turn.left.45.medium',
  'turn.right.45.medium',
  'turn.left.90.medium',
  'turn.right.90.medium',
  'slope.0.25',
  'slope.25.60',
  'slope.60.90',
  'slope.90.60',
  'slope.60.25',
  'slope.25.0',
  'slope.0.n25',
  'slope.n25.n60',
  'slope.n60.n90',
  'slope.n90.n60',
  'slope.n60.n25',
  'slope.n25.0',
  'slope.25.hold.run0_5t',
  'slope.25.hold.run1t',
  'slope.25.hold.run2t',
  'slope.25.hold.run3t',
  'slope.n25.hold.run0_5t',
  'slope.n25.hold.run1t',
  'slope.n25.hold.run2t',
  'slope.n25.hold.run3t',
  'slope.60.hold.run0_5t',
  'slope.60.hold.run1t',
  'slope.60.hold.run2t',
  'slope.60.hold.run3t',
  'slope.n60.hold.run0_5t',
  'slope.n60.hold.run1t',
  'slope.n60.hold.run2t',
  'slope.n60.hold.run3t',
  'slope.90.hold.rise0_5t',
  'slope.90.hold.rise1t',
  'slope.90.hold.rise2t',
  'slope.90.hold.rise3t',
  'slope.n90.hold.rise0_5t',
  'slope.n90.hold.rise1t',
  'slope.n90.hold.rise2t',
  'slope.n90.hold.rise3t',
  'bank.level.left.1t',
  'bank.left.level.1t',
  'bank.level.right.1t',
  'bank.right.level.1t',
]);

const FINISH_DESCRIPTORS = AUTHORED_TRACK_CATALOG.filter((descriptor) =>
  FINISH_DESCRIPTOR_IDS.has(descriptor.id),
);

function poseKey(pose: AuthoredGridPose): string {
  return [
    pose.x,
    pose.y,
    pose.z,
    pose.headingStep,
    pose.pitchDegrees,
    pose.bank,
    pose.orientation,
  ].join('|');
}

function heuristic(pose: AuthoredGridPose, target: AuthoredGridPose): number {
  const spatial =
    (Math.abs(pose.x - target.x) + Math.abs(pose.y - target.y) + Math.abs(pose.z - target.z)) / 32;
  const headingDelta = Math.min(
    Math.abs(pose.headingStep - target.headingStep),
    8 - Math.abs(pose.headingStep - target.headingStep),
  );
  const pitchPenalty = pose.pitchDegrees === target.pitchDegrees ? 0 : 3;
  const bankPenalty = pose.bank === target.bank ? 0 : 2;
  return spatial + headingDelta + pitchPenalty + bankPenalty;
}

function inBounds(project: CoasterProject, pose: AuthoredGridPose): boolean {
  const halfX = Math.floor((project.plot.sizeMeters.x / 2) * 8);
  const halfZ = Math.floor((project.plot.sizeMeters.z / 2) * 8);
  const ground = Math.round(project.plot.groundElevationMeters * 8);
  return (
    pose.x >= -halfX &&
    pose.x <= halfX &&
    pose.z >= -halfZ &&
    pose.z <= halfZ &&
    pose.y >= ground + 16 &&
    pose.y <= ground + 1_600
  );
}

interface SearchState {
  readonly pose: AuthoredGridPose;
  readonly path: readonly string[];
  readonly score: number;
}

function compareSearchStates(first: SearchState, second: SearchState): number {
  return first.score - second.score || first.path.length - second.path.length;
}

function pushSearchState(queue: SearchState[], state: SearchState): void {
  queue.push(state);
  let index = queue.length - 1;
  while (index > 0) {
    const parentIndex = Math.floor((index - 1) / 2);
    const parent = queue[parentIndex]!;
    if (compareSearchStates(parent, state) <= 0) break;
    queue[index] = parent;
    index = parentIndex;
  }
  queue[index] = state;
}

function popSearchState(queue: SearchState[]): SearchState | undefined {
  const first = queue[0];
  const last = queue.pop();
  if (!first || !last || queue.length === 0) return first;
  let index = 0;
  while (true) {
    const leftIndex = index * 2 + 1;
    if (leftIndex >= queue.length) break;
    const rightIndex = leftIndex + 1;
    const childIndex =
      rightIndex < queue.length && compareSearchStates(queue[rightIndex]!, queue[leftIndex]!) < 0
        ? rightIndex
        : leftIndex;
    if (compareSearchStates(last, queue[childIndex]!) <= 0) break;
    queue[index] = queue[childIndex]!;
    index = childIndex;
  }
  queue[index] = last;
  return first;
}

function appendPath(
  project: CoasterProject,
  descriptorIds: readonly string[],
): AuthoredBuildResult<CoasterProject> {
  let candidate = project;
  for (const descriptorId of descriptorIds) {
    const appended = appendAuthoredPiece(candidate, descriptorId);
    if (!appended.ok) return appended;
    candidate = appended.value;
  }
  return { ok: true, value: candidate };
}

function planAuthoredRoute(
  project: CoasterProject,
  limits: AuthoredFinishLimits = {},
): AuthoredBuildResult<AuthoredFinishPlan> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This finish solver only supports authored-grid tracks.',
    };
  if (project.track.closed)
    return { ok: false, code: 'already_closed', message: 'This circuit is already closed.' };
  const component = project.track.authoredTopology.components[0];
  const pieceById = new Map(project.track.pieces.map((piece) => [piece.id, piece]));
  const firstPiece = component ? pieceById.get(component.pieceIds[0]!) : null;
  const nextComponent = project.track.authoredTopology.components.find(
    (candidate) => candidate.id !== component?.id,
  );
  const nextComponentHead = nextComponent ? pieceById.get(nextComponent.pieceIds[0]!) : null;
  const station = project.track.pieces.find(
    (piece) => getAuthoredDescriptor(piece.descriptorId)?.category === 'station',
  );
  const start = authoredEndPose(project);
  if (!station || !component || !firstPiece || !start)
    return { ok: false, code: 'station_required', message: 'Place a station first.' };
  if (project.track.pieces.length < 4)
    return {
      ok: false,
      code: 'needs_track',
      message: 'Build at least three pieces after the station first.',
    };
  const target = nextComponentHead?.entryPose ?? firstPiece.entryPose;
  const maxPieces = limits.maxPieces ?? 12;
  const maxStates = limits.maxStates ?? 25_000;
  const deadline = Date.now() + (limits.timeBudgetMs ?? 100);
  const queue: SearchState[] = [{ pose: start, path: [], score: heuristic(start, target) }];
  const bestDepth = new Map<string, number>([[poseKey(start), 0]]);
  let exploredStates = 0;
  let lastRejectedRoute: string | null = null;

  while (queue.length && exploredStates < maxStates && Date.now() <= deadline) {
    const current = popSearchState(queue)!;
    exploredStates += 1;
    if (posesConnect(current.pose, target)) {
      const appended = appendPath(project, current.path);
      if (!appended.ok) {
        if (appended.code === 'track_overlap') {
          lastRejectedRoute = appended.message;
          continue;
        }
        return appended;
      }
      const candidate = appended.value;
      const repairedComponentGap = project.track.authoredTopology.components.length > 1;
      const completed =
        candidate.track.closed || repairedComponentGap
          ? ({ ok: true, value: candidate } as const)
          : closeAuthoredCircuit(candidate);
      if (!completed.ok) return completed;
      const rejection = limits.validateProject?.(completed.value) ?? null;
      if (rejection) {
        lastRejectedRoute = rejection;
        continue;
      }
      return {
        ok: true,
        value: {
          descriptorIds: current.path,
          exploredStates,
          project: completed.value,
          mode: 'standard',
          customTailSectionIds: [],
        },
      };
    }
    if (current.path.length >= maxPieces) continue;
    for (const descriptor of FINISH_DESCRIPTORS) {
      if (!descriptor.entry.pitches.includes(current.pose.pitchDegrees)) continue;
      if (!descriptor.entry.banks.includes(current.pose.bank)) continue;
      if (!descriptor.entry.orientations.includes(current.pose.orientation)) continue;
      if (current.path.length === 0 && limits.validateFirstStep) {
        const firstStep = appendAuthoredPiece(project, descriptor.id);
        if (!firstStep.ok || limits.validateFirstStep(firstStep.value)) continue;
      }
      const nextPose = descriptorExitPose(current.pose, descriptor);
      if (!inBounds(project, nextPose)) continue;
      const nextPath = [...current.path, descriptor.id];
      const key = poseKey(nextPose);
      if (!posesConnect(nextPose, target)) {
        if ((bestDepth.get(key) ?? Number.POSITIVE_INFINITY) <= nextPath.length) continue;
        bestDepth.set(key, nextPath.length);
      }
      pushSearchState(queue, {
        pose: nextPose,
        path: nextPath,
        score: nextPath.length + heuristic(nextPose, target),
      });
    }
  }
  return {
    ok: false,
    code: Date.now() > deadline ? 'timeout' : 'no_standard_route',
    message: lastRejectedRoute
      ? `Official-piece routes reach the selected endpoint but fail the build check: ${lastRejectedRoute}`
      : Date.now() > deadline
        ? 'The official-piece route search needs more time from this position. Continue with Route Guide, simplify the approach, and try again.'
        : 'No safe exact route using official pieces reaches the selected endpoint from this height, heading, slope, and bank. Continue with Route Guide and try again.',
  };
}

export function planAuthoredFinish(
  project: CoasterProject,
  limits: AuthoredFinishLimits = {},
): AuthoredBuildResult<AuthoredFinishPlan> {
  return planAuthoredRoute(project, limits);
}

export function planAuthoredAutocomplete(
  project: CoasterProject,
  limits: AuthoredFinishLimits = {},
): AuthoredBuildResult<AuthoredFinishPlan> {
  return planAuthoredRoute(project, limits);
}

export function planAuthoredReverseAutocomplete(
  project: CoasterProject,
  limits: AuthoredFinishLimits = {},
): AuthoredBuildResult<AuthoredFinishPlan> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This finish solver only supports authored-grid tracks.',
    };
  const component = project.track.authoredTopology.components[0];
  const pieceById = new Map(project.track.pieces.map((piece) => [piece.id, piece]));
  const targetComponent =
    project.track.authoredTopology.components.find((candidate) => candidate.id !== component?.id) ??
    component;
  const targetTail = targetComponent ? pieceById.get(targetComponent.pieceIds.at(-1)!) : null;
  const start = authoredStartPose(project, component?.id);
  const target = targetTail?.exitPose;
  if (!component || !start || !target)
    return { ok: false, code: 'needs_track', message: 'Select a repair endpoint first.' };
  const maxPieces = limits.maxPieces ?? 12;
  const maxStates = limits.maxStates ?? 25_000;
  const deadline = Date.now() + (limits.timeBudgetMs ?? 100);
  const queue: SearchState[] = [{ pose: start, path: [], score: heuristic(start, target) }];
  const bestDepth = new Map<string, number>([[poseKey(start), 0]]);
  let exploredStates = 0;
  let lastRejectedRoute: string | null = null;

  const applyPath = (descriptorIds: readonly string[]) => {
    let candidateProject = project;
    for (const descriptorId of descriptorIds) {
      if (candidateProject.track.geometryKind !== 'authored_grid_v2') break;
      const activeComponent = candidateProject.track.authoredTopology.components.find(
        (candidate) => candidate.id === component.id,
      );
      const head = authoredStartPose(candidateProject, activeComponent?.id);
      if (!head)
        return { ok: false as const, code: 'missing_endpoint', message: 'The endpoint moved.' };
      const candidate = createAuthoredCandidateEndingAt(descriptorId, head);
      if (!candidate.ok) return candidate;
      const prepended = prependAuthoredCandidate(
        candidateProject,
        candidate.value,
        activeComponent?.id,
      );
      if (!prepended.ok) return prepended;
      candidateProject = prepended.value;
    }
    return { ok: true as const, value: candidateProject };
  };

  while (queue.length && exploredStates < maxStates && Date.now() <= deadline) {
    const current = popSearchState(queue)!;
    exploredStates += 1;
    if (posesConnect(target, current.pose)) {
      const repaired = applyPath(current.path);
      if (!repaired.ok) {
        lastRejectedRoute = repaired.message;
        continue;
      }
      const rejection = limits.validateProject?.(repaired.value) ?? null;
      if (rejection) {
        lastRejectedRoute = rejection;
        continue;
      }
      return {
        ok: true,
        value: {
          descriptorIds: current.path,
          exploredStates,
          project: repaired.value,
          mode: 'standard',
          customTailSectionIds: [],
        },
      };
    }
    if (current.path.length >= maxPieces) continue;
    for (const descriptor of FINISH_DESCRIPTORS) {
      const candidate = createAuthoredCandidateEndingAt(descriptor.id, current.pose);
      if (!candidate.ok || !inBounds(project, candidate.value.entryPose)) continue;
      const nextPath = [...current.path, descriptor.id];
      const key = poseKey(candidate.value.entryPose);
      if (!posesConnect(target, candidate.value.entryPose)) {
        if ((bestDepth.get(key) ?? Number.POSITIVE_INFINITY) <= nextPath.length) continue;
        bestDepth.set(key, nextPath.length);
      }
      pushSearchState(queue, {
        pose: candidate.value.entryPose,
        path: nextPath,
        score: nextPath.length + heuristic(candidate.value.entryPose, target),
      });
    }
  }
  return {
    ok: false,
    code: Date.now() > deadline ? 'timeout' : 'no_standard_route',
    message: lastRejectedRoute
      ? `Official-piece routes reach the opposite endpoint but fail the build check: ${lastRejectedRoute}`
      : 'No exact reverse route using official pieces reaches the opposite endpoint. Switch sides or continue manually.',
  };
}
